/**
 * Locale-aware formatting.
 *
 * A widget on a German broker's site must read 1.234,56 and 14. August; on a
 * British one, 1,234.56 and 14 August. All of it flows from the single `locale`
 * URL parameter, and Intl formatters are memoised because constructing one is
 * expensive and a ticker re-renders often.
 */

const numberCache = new Map<string, Intl.NumberFormat>();
const dateCache = new Map<string, Intl.DateTimeFormat>();

function numberFormat(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let f = numberCache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale, options);
    numberCache.set(key, f);
  }
  return f;
}

function dateFormat(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let f = dateCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, options);
    dateCache.set(key, f);
  }
  return f;
}

/**
 * FX rates carry a conventional number of decimals: 5 for most pairs, 3 for
 * anything quoted in JPY. Showing EUR/USD as 1.16 rather than 1.15670 would
 * look wrong to anyone who trades it.
 */
export function formatRate(value: number, quote: string, locale: string): string {
  const dp = quote === 'JPY' ? 3 : 5;
  return numberFormat(locale, {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  }).format(value);
}

/**
 * Crypto prices span eight orders of magnitude, so a fixed precision is wrong
 * at one end or the other: BTC at 62,640.00 and DOGE at 0.09 need different
 * treatment. Precision is chosen from magnitude.
 */
export function formatPrice(value: number, currency: string, locale: string): string {
  const abs = Math.abs(value);
  const dp = abs >= 1000 ? 2 : abs >= 1 ? 2 : abs >= 0.01 ? 4 : 6;
  try {
    return numberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    }).format(value);
  } catch {
    // An unknown currency code makes Intl throw; a bare number beats a blank.
    return numberFormat(locale, {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    }).format(value);
  }
}

export function formatPercent(value: number, locale: string): string {
  return numberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    signDisplay: 'exceptZero',
  }).format(value);
}

/** Compact market caps: 1.26T rather than 1,257,017,830,116. */
export function formatCompact(value: number, locale: string): string {
  return numberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatTime(iso: string, locale: string, timeZone: string): string {
  return dateFormat(locale, { hour: '2-digit', minute: '2-digit', timeZone }).format(
    new Date(iso),
  );
}

/**
 * Render a day key ('2026-08-18') produced by groupByDay().
 *
 * The key is already a calendar date in the target zone, so it must NOT be
 * converted again. It is materialised at noon UTC and formatted in UTC:
 * midday is far enough from either boundary that no offset can push it across
 * a date line, and forcing timeZone UTC stops the formatter re-shifting a date
 * that has already been shifted once.
 */
export function formatDayHeading(dayKey: string, locale: string): string {
  return dateFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${dayKey}T12:00:00Z`));
}

/**
 * The "as of" stamp in a widget header.
 *
 * Deliberately relative for anything recent — "2 min ago" answers the question
 * a trader is actually asking ("is this live?") far better than a wall-clock
 * time they would have to subtract from. Older stamps fall back to a date,
 * where the absolute value is what matters.
 */
export function formatStamp(iso: string, locale: string, timeZone?: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';

  const seconds = Math.round((Date.now() - then) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });

  if (seconds < 45) return rtf.format(0, 'second').replace(/^0 /, '');
  if (seconds < 3600) return rtf.format(-Math.round(seconds / 60), 'minute');
  if (seconds < 86_400) return rtf.format(-Math.round(seconds / 3600), 'hour');

  return dateFormat(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(iso));
}

/**
 * Group ISO timestamps by calendar day in a specific timezone.
 *
 * The timezone must be the same one the times are rendered in, or the widget
 * contradicts itself: an 03:30 IST release is on the 18th in Kolkata and the
 * 17th in UTC, so grouping in UTC while printing IST times puts "03:30" under
 * the previous day's heading. That is the bug the `tz` parameter exists to
 * avoid, and it is why grouping and formatting share one zone rather than each
 * picking their own default.
 *
 * The day key is derived with the en-CA locale, whose short date format is
 * exactly YYYY-MM-DD — cheaper and less error-prone than reconstructing a local
 * date from parts, and it gets the zone's own DST rules for free.
 */
export function groupByDay<T>(
  items: T[],
  getDate: (item: T) => string,
  timeZone: string,
): [string, T[]][] {
  const keyFormat = dateFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  });

  const groups = new Map<string, T[]>();
  for (const item of items) {
    const at = new Date(getDate(item));
    if (!Number.isFinite(at.getTime())) continue;
    const day = keyFormat.format(at);
    const bucket = groups.get(day);
    if (bucket) bucket.push(item);
    else groups.set(day, [item]);
  }
  // Map preserves insertion order, and the API returns events already sorted by
  // time, so the day keys come out chronological without a re-sort.
  return [...groups.entries()];
}
